<template>
  <div class="instructor-profile-page">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
      </div>
      <p class="loading-text">
        {{ $t("common.loading") || "جاري التحميل..." }}
      </p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <h3>{{ $t("common.error") || "حدث خطأ" }}</h3>
      <p>{{ error }}</p>
      <button @click="fetchInstructorDetails" class="retry-btn">
        <i class="fas fa-redo"></i>
        <span>{{ $t("common.retry") || "إعادة المحاولة" }}</span>
      </button>
    </div>

    <!-- Profile Content -->
    <template v-else-if="instructor">
      <!-- Hero Section -->
      <div class="profile-hero">
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="profile-avatar-section">
            <div class="avatar-glow"></div>
            <div class="profile-avatar">
              <img
                :src="instructor.user_img"
                :alt="instructor.name"
                @error="setDefaultImage"
              />
            </div>
            <div class="verified-badge">
              <i class="fas fa-check"></i>
            </div>
          </div>

          <h1 class="profile-name">{{ instructor.name }}</h1>

          <div class="profile-badge">
            <i class="fas fa-graduation-cap"></i>
            <span>{{
              $t("instructors.platform_badge") || "محاضر معتمد في منصة أكاديميا"
            }}</span>
          </div>

          <div class="profile-rating">
            <div class="stars">
              <i class="fas fa-star" v-for="n in 5" :key="n"></i>
            </div>
            <span class="rating-text">{{
              $t("instructors.excellent_rating") || "تقييم ممتاز"
            }}</span>
          </div>

          <!-- Action Buttons -->
          <div class="profile-actions">
            <button @click="shareProfile" class="share-btn">
              <i class="fas fa-share-alt"></i>
              <span>{{
                $t("instructors.share_profile") || "مشاركة الملف الشخصي"
              }}</span>
            </button>
            <!-- Friend Request / Chat Button -->
            <button
              v-if="isLoadingUserStatus"
              class="chat-invite-btn"
              disabled
            >
              <i class="fas fa-spinner fa-spin"></i>
              <span>{{ $t("common.loading") || "جاري التحميل..." }}</span>
            </button>
            <button
              v-else-if="userStatus.conversation_id"
              @click="goToChat"
              class="chat-invite-btn chat-exists"
            >
              <i class="fas fa-comments"></i>
              <span>{{ $t("instructors.open_chat") || "فتح المحادثة" }}</span>
              <span v-if="userStatus.is_online" class="online-badge">
                <i class="fas fa-circle"></i>
              </span>
            </button>
            <button
              v-else
              @click="openAddFriendDialog"
              class="chat-invite-btn"
            >
              <i class="fas fa-user-plus"></i>
              <span>{{ $t("instructors.invite_to_chat") || "إرسال طلب صداقة" }}</span>
            </button>
          </div>
        </div>

        <div class="hero-decoration">
          <div class="circle circle-1"></div>
          <div class="circle circle-2"></div>
          <div class="circle circle-3"></div>
        </div>
      </div>

      <!-- Stats Section -->
      <div class="profile-stats">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-book"></i>
          </div>
          <div class="stat-info">
            <span class="stat-number">
              <span v-if="isLoadingCourses" class="stat-loader"></span>
              <template v-else>{{ totalCourses }}</template>
            </span>
            <span class="stat-label">{{
              $t("instructors.courses_count") || "الدورات"
            }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bundles">
            <i class="fas fa-layer-group"></i>
          </div>
          <div class="stat-info">
            <span class="stat-number">
              <span v-if="isLoadingBundles" class="stat-loader"></span>
              <template v-else>{{ totalBundles }}</template>
            </span>
            <span class="stat-label">{{
              $t("instructors.bundles_count") || "الباقات"
            }}</span>
          </div>
        </div>
        <div class="stat-card" v-if="instructor.students_count">
          <div class="stat-icon students">
            <i class="fas fa-users"></i>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{ instructor.students_count }}</span>
            <span class="stat-label">{{
              $t("instructors.students_count") || "الطلاب"
            }}</span>
          </div>
        </div>
      </div>

      <!-- Bio Section -->
      <div class="profile-bio" v-if="instructor.details">
        <h3 class="section-title">
          <i class="fas fa-user"></i>
          <span>{{ $t("instructors.about") || "نبذة عن المحاضر" }}</span>
        </h3>
        <p class="bio-text">{{ instructor.details }}</p>
      </div>

      <!-- Tabs Section -->
      <div class="profile-tabs">
        <div class="tabs-header">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'courses' }"
            @click="activeTab = 'courses'"
          >
            <i class="fas fa-book"></i>
            <span>{{ $t("instructors.courses") || "الدورات" }}</span>
            <span class="tab-count">{{ totalCourses }}</span>
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'bundles' }"
            @click="activeTab = 'bundles'"
          >
            <i class="fas fa-layer-group"></i>
            <span>{{ $t("instructors.bundles") || "الباقات" }}</span>
            <span class="tab-count">{{ totalBundles }}</span>
          </button>
        </div>

        <!-- Courses Tab -->
        <div
          v-show="activeTab === 'courses'"
          class="tab-content"
          ref="coursesTab"
        >
          <!-- Skeleton Loading -->
          <div v-if="isLoadingCourses" class="items-grid skeleton-grid">
            <div
              v-for="n in skeletonCount"
              :key="'skeleton-course-' + n"
              class="item-card skeleton-card"
            >
              <div class="skeleton-image"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-price"></div>
              </div>
            </div>
          </div>

          <div
            v-else-if="!courses.data || courses.data.length === 0"
            class="tab-empty"
          >
            <i class="fas fa-book-open"></i>
            <p>{{ $t("instructors.no_courses") || "لا توجد دورات حالياً" }}</p>
          </div>

          <transition-group
            v-else
            name="card-fade"
            tag="div"
            class="items-grid"
          >
            <div
              v-for="(course, index) in courses.data"
              :key="course.id"
              class="item-card"
              :style="{ animationDelay: `${index * 0.05}s` }"
            >
              <div class="item-image">
                <img
                  :src="course.preview_image"
                  :alt="course.title"
                  @error="setDefaultImage"
                  loading="lazy"
                />
                <div class="item-overlay">
                  <router-link
                    :to="`/course-details?id=${course.id}`"
                    class="view-btn"
                  >
                    <i class="fas fa-play"></i>
                  </router-link>
                </div>
              </div>
              <div class="item-content">
                <h4 class="item-title">
                  <router-link :to="`/course-details?id=${course.id}`">{{
                    course.title
                  }}</router-link>
                </h4>
                <!-- Institute Badge -->
                <div v-if="course.institute" class="item-meta">
                  <span class="institute-badge">
                    <i class="fas fa-university"></i>
                    {{ course.institute.title }}
                  </span>
                </div>
                <!-- Last Update -->
                <div v-if="course.last_update" class="item-meta last-update">
                  <i class="fas fa-calendar-alt"></i>
                  <span
                    >{{ $t("instructors.last_update") || "آخر تحديث" }}:
                    {{ formatDate(course.last_update) }}</span
                  >
                </div>
                <div class="item-price">
                  <template
                    v-if="
                      (course.price === null || course.price === 0) && (course.discount_price === null || course.discount_price === 0)
                    "
                  >
                    <span class="price free">{{
                      $t("common.free") || "مجاني"
                    }}</span>
                  </template>
                  <template v-else-if="course.discount_price">
                    <span class="price current"
                      >{{ course.discount_price }} {{ $appCurrency }}</span
                    >
                    <span class="price old"
                      >{{ course.price }} {{ $appCurrency }}</span
                    >
                    <span class="discount-badge"
                      >{{
                        calculateDiscount(course.price, course.discount_price)
                      }}%</span
                    >
                  </template>
                  <template v-else>
                    <span class="price current"
                      >{{ course.price }} {{ $appCurrency }}</span
                    >
                  </template>
                </div>
                <!-- Add to Cart Button for Courses -->
                <button
                  v-if="(course.price != null && course.price !== 0) || (course.discount_price != null && course.discount_price !== 0)"
                  class="add-to-cart-btn"
                  @click="handleAddToCart(course.id, $event, 'course')"
                >
                  <i class="fas fa-cart-plus"></i>
                  <span>{{ $t("bundle.add_to_cart") || "إضافة للسلة" }}</span>
                </button>
              </div>
            </div>
          </transition-group>

          <!-- Courses Pagination -->
          <div
            v-if="courses.meta && courses.meta.last_page > 1"
            class="tab-pagination"
          >
            <button
              class="page-btn"
              :disabled="courses.meta.current_page <= 1 || isLoadingCourses"
              @click="changePage('courses', courses.meta.current_page - 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="page-info"
              >{{ courses.meta.current_page }} /
              {{ courses.meta.last_page }}</span
            >
            <button
              class="page-btn"
              :disabled="
                courses.meta.current_page >= courses.meta.last_page ||
                isLoadingCourses
              "
              @click="changePage('courses', courses.meta.current_page + 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>

        <!-- Bundles Tab -->
        <div
          v-show="activeTab === 'bundles'"
          class="tab-content"
          ref="bundlesTab"
        >
          <!-- Skeleton Loading -->
          <div v-if="isLoadingBundles" class="items-grid skeleton-grid">
            <div
              v-for="n in skeletonCount"
              :key="'skeleton-bundle-' + n"
              class="item-card skeleton-card"
            >
              <div class="skeleton-image"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-price"></div>
              </div>
            </div>
          </div>

          <div
            v-else-if="!bundles.data || bundles.data.length === 0"
            class="tab-empty"
          >
            <i class="fas fa-layer-group"></i>
            <p>{{ $t("instructors.no_bundles") || "لا توجد باقات حالياً" }}</p>
          </div>

          <transition-group
            v-else
            name="card-fade"
            tag="div"
            class="items-grid"
          >
            <div
              v-for="(bundle, index) in bundles.data"
              :key="bundle.id"
              class="item-card bundle-card"
              :style="{ animationDelay: `${index * 0.05}s` }"
            >
              <div class="item-image">
                <img
                  :src="bundle.preview_image"
                  :alt="bundle.title"
                  @error="setDefaultImage"
                  loading="lazy"
                />
                <div class="bundle-badge">
                  <i class="fas fa-layer-group"></i>
                  <span>{{ $t("common.bundle") || "باقة" }}</span>
                </div>
                <div class="item-overlay">
                  <router-link
                    :to="`/bundle-details?id=${bundle.id}`"
                    class="view-btn"
                  >
                    <i class="fas fa-eye"></i>
                  </router-link>
                </div>
              </div>
              <div class="item-content">
                <h4 class="item-title">
                  <router-link :to="`/bundle-details?id=${bundle.id}`">{{
                    bundle.title
                  }}</router-link>
                </h4>
                <!-- Bundle Courses Count -->
                <div
                  v-if="bundle.courses && bundle.courses.length"
                  class="item-meta"
                >
                  <span class="courses-count-badge">
                    <i class="fas fa-book"></i>
                    {{ bundle.courses.length }}
                    {{ $t("instructors.courses") || "دورات" }}
                  </span>
                </div>
                <div class="item-price">
                  <template
                    v-if="
                      (bundle.price === null || bundle.price === 0) && (bundle.discount_price === null || bundle.discount_price === 0)
                    "
                  >
                    <span class="price free">{{
                      $t("common.free") || "مجاني"
                    }}</span>
                  </template>
                  <template v-else-if="bundle.discount_price">
                    <span class="price current"
                      >{{ bundle.discount_price }} {{ $appCurrency }}</span
                    >
                    <span class="price old"
                      >{{ bundle.original_price || bundle.price }}
                      {{ $appCurrency }}</span
                    >
                    <span class="discount-badge"
                      >{{
                        bundle.discount_percentage ||
                        calculateDiscount(bundle.price, bundle.discount_price)
                      }}%</span
                    >
                  </template>
                  <template v-else>
                    <span class="price current"
                      >{{ bundle.price }} {{ $appCurrency }}</span
                    >
                  </template>
                </div>
                <!-- Add to Cart Button for Bundles -->
                <button
                  v-if="(bundle.price != null && bundle.price !== 0) || (bundle.discount_price != null && bundle.discount_price !== 0)"
                  class="add-to-cart-btn"
                  @click="handleAddToCart(bundle.id, $event, 'bundle')"
                >
                  <i class="fas fa-cart-plus"></i>
                  <span>{{ $t("bundle.add_to_cart") || "إضافة للسلة" }}</span>
                </button>
              </div>
            </div>
          </transition-group>

          <!-- Bundles Pagination -->
          <div
            v-if="bundles.meta && bundles.meta.last_page > 1"
            class="tab-pagination"
          >
            <button
              class="page-btn"
              :disabled="bundles.meta.current_page <= 1 || isLoadingBundles"
              @click="changePage('bundles', bundles.meta.current_page - 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="page-info"
              >{{ bundles.meta.current_page }} /
              {{ bundles.meta.last_page }}</span
            >
            <button
              class="page-btn"
              :disabled="
                bundles.meta.current_page >= bundles.meta.last_page ||
                isLoadingBundles
              "
              @click="changePage('bundles', bundles.meta.current_page + 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Share Modal -->
    <div
      v-if="showShareModal"
      class="share-modal-overlay"
      @click.self="showShareModal = false"
    >
      <div class="share-modal">
        <button class="close-modal" @click="showShareModal = false">
          <i class="fas fa-times"></i>
        </button>
        <h3>{{ $t("instructors.share_profile") || "مشاركة الملف الشخصي" }}</h3>
        <div class="share-link-box">
          <input type="text" :value="shareUrl" readonly ref="shareInput" />
          <button @click="copyShareLink" class="copy-btn">
            <i :class="copied ? 'fas fa-check' : 'fas fa-copy'"></i>
          </button>
        </div>
        <div class="share-buttons">
          <a
            :href="`https://wa.me/?text=${encodeURIComponent(shareText)}`"
            target="_blank"
            class="share-social whatsapp"
          >
            <i class="fab fa-whatsapp"></i>
          </a>
          <a
            :href="`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              shareText
            )}`"
            target="_blank"
            class="share-social twitter"
          >
            <i class="fab fa-twitter"></i>
          </a>
          <a
            :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareUrl
            )}`"
            target="_blank"
            class="share-social facebook"
          >
            <i class="fab fa-facebook-f"></i>
          </a>
          <a
            :href="`https://t.me/share/url?url=${encodeURIComponent(
              shareUrl
            )}&text=${encodeURIComponent(instructor?.name || '')}`"
            target="_blank"
            class="share-social telegram"
          >
            <i class="fab fa-telegram-plane"></i>
          </a>
        </div>
      </div>
    </div>

    <!-- Chat Invite Modal -->
    <div v-if="showChatModal" class="chat-modal-overlay" @click.self="closeChatModal">
      <div class="chat-modal">
        <div class="chat-modal-header">
          <div class="chat-modal-icon">
            <i class="fas fa-comment-dots"></i>
          </div>
          <h3>{{ $t("instructors.invite_to_chat") || "دعوة للمحادثة" }}</h3>
          <button class="chat-modal-close" @click="closeChatModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="chat-modal-body">
          <!-- Loading State -->
          <div v-if="chatLoading" class="chat-modal-loading">
            <div class="spinner"></div>
            <p>{{ $t("common.loading") || "جاري التحميل..." }}</p>
          </div>
          
          <!-- Already have conversation -->
          <div v-else-if="existingConversation" class="chat-modal-existing">
            <div class="existing-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h4>{{ $t("instructors.conversation_exists") || "لديك محادثة مع هذا المحاضر" }}</h4>
            <p>{{ $t("instructors.go_to_conversation") || "يمكنك الذهاب للمحادثة مباشرة" }}</p>
            <button class="go-to-chat-btn" @click="goToConversation">
              <i class="fas fa-comments"></i>
              {{ $t("instructors.open_chat") || "فتح المحادثة" }}
            </button>
          </div>
          
          <!-- Send Request Form -->
          <div v-else class="chat-modal-form">
            <!-- Instructor Preview with Shareable ID -->
            <div class="instructor-preview">
              <img :src="instructor?.user_img" @error="setDefaultImage" alt="" />
              <div class="instructor-preview-info">
                <span class="instructor-preview-name">{{ instructor?.name }}</span>
                <span class="instructor-preview-id" v-if="instructorShareableId">
                  <i class="fas fa-id-badge"></i>
                  {{ instructorShareableId }}
                </span>
              </div>
            </div>
            
            <!-- Confirmation Message -->
            <div class="confirmation-box">
              <i class="fas fa-info-circle"></i>
              <p>{{ $t("instructors.chat_confirm_message") || "هل تريد إرسال طلب محادثة لهذا المحاضر؟" }}</p>
            </div>
            
            <div class="form-field">
              <label>{{ $t("chat.request_message") || "رسالة الطلب" }} <span class="optional">({{ $t("common.optional") || "اختياري" }})</span></label>
              <textarea
                v-model="chatMessage"
                :placeholder="$t('chat.request_message_hint') || 'مرحباً، أريد التواصل معك'"
                rows="3"
              ></textarea>
            </div>
            
            <div v-if="chatError" class="chat-error">
              <i class="fas fa-exclamation-circle"></i>
              {{ chatError }}
            </div>
            
            <div v-if="chatSuccess" class="chat-success">
              <i class="fas fa-check-circle"></i>
              {{ $t("chat.request_sent_successfully") || "تم إرسال الطلب بنجاح!" }}
            </div>
          </div>
        </div>
        
        <div class="chat-modal-footer" v-if="!chatLoading && !existingConversation && !chatSuccess">
          <button class="cancel-btn" @click="closeChatModal">
            {{ $t("common.cancel") || "إلغاء" }}
          </button>
          <button class="send-btn" @click="sendChatRequest" :disabled="sendingRequest">
            <i v-if="sendingRequest" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-paper-plane"></i>
            {{ sendingRequest ? ($t("chat.sending") || "جاري الإرسال...") : ($t("chat.send_request") || "إرسال الطلب") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Friend Dialog -->
    <AddFriendDialog
      v-if="showAddFriendDialog"
      :initial-shareable-id="userStatus.shareable_id || ''"
      :instructor-name="instructor?.name || ''"
      @close="showAddFriendDialog = false"
      @request-sent="onFriendRequestSent"
    />
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";
import { addToCart } from "@/utils/cart";
import { ChatService } from "@/services/chat";
import AddFriendDialog from "@/components/chat/AddFriendDialog.vue";

export default {
  name: "InstructorProfilePage",
  components: {
    AddFriendDialog,
  },
  data() {
    return {
      instructorId: null,
      instructor: null,
      courses: { data: [] },
      bundles: { data: [] },
      isLoading: true,
      isLoadingCourses: false,
      isLoadingBundles: false,
      error: null,
      activeTab: "courses",
      showShareModal: false,
      copied: false,
      defaultImage: "/assets/img/acad-icon.png",
      // Chat invite
      showChatModal: false,
      chatLoading: false,
      existingConversation: null,
      chatMessage: "",
      chatError: null,
      chatSuccess: false,
      sendingRequest: false,
      instructorShareableId: null,
      // Friend request / user status
      userStatus: {
        is_online: false,
        conversation_id: null,
        shareable_id: null,
      },
      isLoadingUserStatus: false,
      isSendingFriendRequest: false,
      showAddFriendDialog: false,
    };
  },
  computed: {
    shareUrl() {
      return window.location.href;
    },
    shareText() {
      return `${this.instructor?.name || ""} - ${
        this.$t("instructors.platform_badge") || "محاضر في منصة أكاديميا"
      }\n${this.shareUrl}`;
    },
    totalCourses() {
      return this.courses.meta?.total || this.courses.data?.length || 0;
    },
    totalBundles() {
      return this.bundles.meta?.total || this.bundles.data?.length || 0;
    },
    skeletonCount() {
      // Show same number of skeletons as items per page or default to 8
      return this.courses.meta?.per_page || this.bundles.meta?.per_page || 8;
    },
  },
  mounted() {
    this.instructorId = this.$route.query.id;
    if (this.instructorId) {
      this.fetchInstructorDetails();
      this.fetchInstructorCourses();
      this.fetchInstructorBundles();
      this.fetchUserStatus();
    } else {
      this.error = this.$t("instructors.no_id") || "لم يتم تحديد المحاضر";
      this.isLoading = false;
    }
  },
  methods: {
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },

    async fetchInstructorDetails() {
      try {
        this.isLoading = true;
        this.error = null;

        const token = localStorage.getItem("access_token");
        const headers = {
          "Content-Type": "application/json",
          "x-country-code": window.appRegion?.phone_code || "SA",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(
          `${apiUrl}/instructors/${this.instructorId}`,
          {
            method: "GET",
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(
            this.$t("instructors.fetch_error") || "فشل في جلب بيانات المحاضر"
          );
        }

        const data = await response.json();
        this.instructor = data.data;
      } catch (error) {
        console.error("Error fetching instructor details:", error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchInstructorCourses(page = 1) {
      try {
        this.isLoadingCourses = true;

        const token = localStorage.getItem("access_token");
        const headers = {
          "Content-Type": "application/json",
          "x-country-code": window.appRegion?.phone_code || "SA",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const res = await fetch(
          `${apiUrl}/courses/all?user_id=${this.instructorId}&page=${page}`,
          { headers }
        );

        const data = await res.json();
        this.courses = data;
      } catch (err) {
        console.error("Error fetching courses:", err);
      } finally {
        this.isLoadingCourses = false;
      }
    },

    async fetchInstructorBundles(page = 1) {
      try {
        this.isLoadingBundles = true;

        const token = localStorage.getItem("access_token");
        const headers = {
          "Content-Type": "application/json",
          "x-country-code": window.appRegion?.phone_code || "SA",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const res = await fetch(
          `${apiUrl}/bundles?user_id=${this.instructorId}&page=${page}`,
          { headers }
        );

        const result = await res.json();
        this.bundles = result;
      } catch (err) {
        console.error("Error fetching bundles:", err);
      } finally {
        this.isLoadingBundles = false;
      }
    },

    changePage(type, page) {
      // Smooth scroll to tabs section
      this.$nextTick(() => {
        const tabsSection = document.querySelector(".profile-tabs");
        if (tabsSection) {
          const offset =
            tabsSection.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: offset, behavior: "smooth" });
        }
      });

      if (type === "courses") {
        this.fetchInstructorCourses(page);
      } else {
        this.fetchInstructorBundles(page);
      }
    },

    calculateDiscount(original, discount) {
      if (!original || !discount) return 0;
      return Math.round(((original - discount) / original) * 100);
    },

    shareProfile() {
      this.showShareModal = true;
      this.copied = false;
    },

    copyShareLink() {
      if (this.$refs.shareInput) {
        this.$refs.shareInput.select();
        document.execCommand("copy");
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },

    handleAddToCart(itemId, event, itemType) {
      addToCart(itemId, event, itemType);
    },

    // Chat invite methods
    async openChatInvite() {
      this.showChatModal = true;
      this.chatLoading = true;
      this.existingConversation = null;
      this.chatMessage = "";
      this.chatError = null;
      this.chatSuccess = false;
      this.instructorShareableId = null;

      try {
        // Check if conversation exists with this instructor
        const result = await ChatService.checkConversation(this.instructorId);
        
        if (result && result.data) {
          // Conversation exists
          this.existingConversation = result.data;
        } else if (result && result.shareable_id) {
          // No conversation, but we have shareable_id
          this.instructorShareableId = result.shareable_id;
        }
      } catch (error) {
        console.error("Error checking conversation:", error);
      } finally {
        this.chatLoading = false;
      }
    },

    closeChatModal() {
      this.showChatModal = false;
      this.existingConversation = null;
      this.chatMessage = "";
      this.chatError = null;
      this.chatSuccess = false;
    },

    goToConversation() {
      if (this.existingConversation?.id) {
        this.$router.push({
          name: "ChatConversation",
          params: { id: this.existingConversation.id },
        });
      }
      this.closeChatModal();
    },

    async sendChatRequest() {
      if (this.sendingRequest) return;
      
      this.sendingRequest = true;
      this.chatError = null;

      try {
        // Use shareable_id if available, otherwise use user_id
        let result;
        if (this.instructorShareableId) {
          result = await ChatService.sendRequestViaShareableId(
            this.instructorShareableId,
            this.chatMessage.trim() || null
          );
        } else {
          // Fallback: send request via user_id
          result = await ChatService.sendRequestViaUserId(
            this.instructorId,
            this.chatMessage.trim() || null
          );
        }

        if (result.success) {
          this.chatSuccess = true;
          setTimeout(() => {
            this.closeChatModal();
          }, 2000);
        } else {
          this.chatError = result.message || this.$t("chat.failed_to_send_request") || "فشل في إرسال الطلب";
        }
      } catch (error) {
        console.error("Error sending chat request:", error);
        this.chatError = this.$t("chat.failed_to_send_request") || "فشل في إرسال الطلب";
      } finally {
        this.sendingRequest = false;
      }
    },

    // Friend request methods
    async fetchUserStatus() {
      if (!this.instructorId) return;
      
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      this.isLoadingUserStatus = true;
      try {
        const status = await ChatService.getUserStatus(this.instructorId);
        this.userStatus = status;
        console.log("User status:", status);
      } catch (error) {
        console.error("Error fetching user status:", error);
      } finally {
        this.isLoadingUserStatus = false;
      }
    },

    openAddFriendDialog() {
      this.showAddFriendDialog = true;
    },

    onFriendRequestSent() {
      // Refresh user status after request is sent
      this.fetchUserStatus();
    },

    goToChat() {
      if (this.userStatus.conversation_id) {
        this.$router.push(`/chat?conversation=${this.userStatus.conversation_id}`);
      }
    },
  },
};
</script>

<style scoped>
/* ===== Page Container ===== */
.instructor-profile-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fff5 0%, #e6ffee 100%);
  direction: rtl;
}

/* ===== Loading Overlay ===== */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

.loading-text {
  margin-top: 24px;
  color: #00c744;
  font-size: 1.1rem;
  font-weight: 600;
}

/* ===== Error State ===== */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px;
  text-align: center;
}

.error-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.error-icon i {
  font-size: 40px;
  color: #ef4444;
}

.error-state h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.error-state p {
  color: #6b7280;
  margin: 0 0 24px 0;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 199, 68, 0.3);
}

/* ===== Hero Section ===== */
.profile-hero {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008830 100%);
  padding: 60px 24px;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.profile-avatar-section {
  position: relative;
  margin-bottom: 24px;
}

.avatar-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  height: 160px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.profile-avatar {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  padding: 5px;
  background: rgba(255, 255, 255, 0.3);
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
}

.verified-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.verified-badge i {
  color: #00c744;
  font-size: 16px;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0 0 16px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.2);
  padding: 10px 24px;
  border-radius: 30px;
  color: white;
  font-weight: 600;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
}

.profile-badge i {
  font-size: 18px;
}

.profile-rating {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.stars {
  display: flex;
  gap: 4px;
}

.stars i {
  color: #fbbf24;
  font-size: 20px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.rating-text {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: white;
  color: #00c744;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.share-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.hero-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  left: -50px;
}

.circle-3 {
  width: 100px;
  height: 100px;
  top: 50%;
  left: 20%;
}

/* ===== Stats Section ===== */
.profile-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 0 24px;
  margin-top: -40px;
  position: relative;
  z-index: 10;
  flex-wrap: wrap;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 28px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  min-width: 160px;
}

.stat-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 24px;
  color: #00c744;
}

.stat-icon.bundles {
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
}

.stat-icon.bundles i {
  color: #8b5cf6;
}

.stat-icon.students {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.stat-icon.students i {
  color: #3b82f6;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  min-width: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stat-loader {
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: stat-spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes stat-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
}

/* ===== Bio Section ===== */
.profile-bio {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.section-title i {
  color: #00c744;
}

.bio-text {
  background: white;
  padding: 24px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  color: #4b5563;
  line-height: 1.8;
  font-size: 1rem;
  margin: 0;
}

/* ===== Tabs Section ===== */
.profile-tabs {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 40px;
}

.tabs-header {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  background: white;
  padding: 8px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover {
  background: #f3f4f6;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.tab-count {
  background: rgba(0, 0, 0, 0.1);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
}

.tab-btn.active .tab-count {
  background: rgba(255, 255, 255, 0.2);
}

/* ===== Tab Content ===== */
.tab-content {
  min-height: 400px;
}

/* ===== Skeleton Loading ===== */
.skeleton-grid {
  opacity: 1;
}

.skeleton-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.skeleton-image {
  width: 100%;
  height: 180px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 16px;
}

.skeleton-title {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
  width: 80%;
}

.skeleton-price {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 50%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* ===== Card Fade Transition ===== */
.card-fade-enter-active {
  animation: card-fade-in 0.4s ease-out forwards;
}

.card-fade-leave-active {
  animation: card-fade-out 0.2s ease-in forwards;
  position: absolute;
}

.card-fade-move {
  transition: transform 0.3s ease;
}

@keyframes card-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes card-fade-out {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
}

.tab-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #6b7280;
}

.mini-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.tab-empty {
  text-align: center;
  padding: 60px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.tab-empty i {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 16px;
}

.tab-empty p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

/* ===== Items Grid ===== */
.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.item-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.item-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.12);
}

.item-image {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.item-card:hover .item-image img {
  transform: scale(1.05);
}

.item-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.item-card:hover .item-overlay {
  opacity: 1;
}

.view-btn {
  width: 56px;
  height: 56px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  font-size: 20px;
  transition: all 0.3s ease;
  text-decoration: none;
}

.view-btn:hover {
  transform: scale(1.1);
  background: #00c744;
  color: white;
}

.bundle-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.item-content {
  padding: 20px;
}

.item-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.item-title a {
  color: #1f2937;
  text-decoration: none;
  transition: color 0.3s ease;
}

.item-title a:hover {
  color: #00c744;
}

.item-price {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.price {
  font-weight: 700;
}

.price.free {
  color: #00c744;
  font-size: 1.1rem;
}

.price.current {
  color: #00c744;
  font-size: 1.2rem;
}

.price.old {
  color: #9ca3af;
  text-decoration: line-through;
  font-size: 0.9rem;
  font-weight: 500;
}

.discount-badge {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* ===== Item Meta (Institute & Last Update) ===== */
.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 0.85rem;
  color: #6b7280;
}

.item-meta.last-update {
  color: #9ca3af;
  font-size: 0.8rem;
}

.item-meta.last-update i {
  color: #00c744;
  font-size: 0.9rem;
}

.institute-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  color: #00a83b;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.institute-badge i {
  font-size: 0.85rem;
}

.courses-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #7c3aed;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.courses-count-badge i {
  font-size: 0.85rem;
}

/* ===== Add to Cart Button ===== */
.add-to-cart-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  margin-top: 12px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.25);
}

.add-to-cart-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.35);
}

.add-to-cart-btn:active {
  transform: translateY(0);
}

.add-to-cart-btn i {
  font-size: 1rem;
}

.add-to-cart-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* ===== Tab Pagination ===== */
.tab-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
}

.page-btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #00c744;
  border-radius: 12px;
  color: #00c744;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #00c744;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #d1d5db;
  color: #d1d5db;
}

.page-info {
  font-weight: 600;
  color: #6b7280;
}

/* ===== Share Modal ===== */
.share-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.share-modal {
  background: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 450px;
  width: 100%;
  position: relative;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.close-modal {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 36px;
  height: 36px;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-modal:hover {
  background: #e5e7eb;
}

.share-modal h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 24px 0;
  text-align: center;
}

.share-link-box {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
}

.share-link-box input {
  flex: 1;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.9rem;
  color: #6b7280;
  direction: ltr;
}

.copy-btn {
  width: 50px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  transform: scale(1.05);
}

.share-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.share-social {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  transition: all 0.3s ease;
  text-decoration: none;
}

.share-social:hover {
  transform: translateY(-4px);
}

.share-social.whatsapp {
  background: #25d366;
}

.share-social.twitter {
  background: #1da1f2;
}

.share-social.facebook {
  background: #1877f2;
}

.share-social.telegram {
  background: #0088cc;
}

/* ===== Animations ===== */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.3;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Dark Mode ===== */
[data-bs-theme="dark"] .instructor-profile-page,
[data-theme="dark"] .instructor-profile-page,
.dark .instructor-profile-page {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .stat-card,
[data-theme="dark"] .stat-card,
.dark .stat-card,
[data-bs-theme="dark"] .bio-text,
[data-theme="dark"] .bio-text,
.dark .bio-text,
[data-bs-theme="dark"] .tabs-header,
[data-theme="dark"] .tabs-header,
.dark .tabs-header,
[data-bs-theme="dark"] .item-card,
[data-theme="dark"] .item-card,
.dark .item-card,
[data-bs-theme="dark"] .tab-empty,
[data-theme="dark"] .tab-empty,
.dark .tab-empty {
  background: #1e293b;
}

[data-bs-theme="dark"] .stat-number,
[data-theme="dark"] .stat-number,
.dark .stat-number,
[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title,
[data-bs-theme="dark"] .item-title a,
[data-theme="dark"] .item-title a,
.dark .item-title a {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .tab-btn,
[data-theme="dark"] .tab-btn,
.dark .tab-btn {
  color: #94a3b8;
}

[data-bs-theme="dark"] .tab-btn:hover,
[data-theme="dark"] .tab-btn:hover,
.dark .tab-btn:hover {
  background: #334155;
}

[data-bs-theme="dark"] .skeleton-card,
[data-theme="dark"] .skeleton-card,
.dark .skeleton-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .skeleton-image,
[data-theme="dark"] .skeleton-image,
.dark .skeleton-image,
[data-bs-theme="dark"] .skeleton-title,
[data-theme="dark"] .skeleton-title,
.dark .skeleton-title,
[data-bs-theme="dark"] .skeleton-price,
[data-theme="dark"] .skeleton-price,
.dark .skeleton-price {
  background: linear-gradient(90deg, #2d3748 25%, #3d4a5c 50%, #2d3748 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

[data-bs-theme="dark"] .item-meta,
[data-theme="dark"] .item-meta,
.dark .item-meta {
  color: #94a3b8;
}

[data-bs-theme="dark"] .item-meta.last-update,
[data-theme="dark"] .item-meta.last-update,
.dark .item-meta.last-update {
  color: #64748b;
}

[data-bs-theme="dark"] .institute-badge,
[data-theme="dark"] .institute-badge,
.dark .institute-badge {
  background: linear-gradient(135deg, #1a3d2e 0%, #0d2818 100%);
  color: #4ade80;
}

[data-bs-theme="dark"] .courses-count-badge,
[data-theme="dark"] .courses-count-badge,
.dark .courses-count-badge {
  background: linear-gradient(135deg, #2e1a4a 0%, #1a0d2e 100%);
  color: #a78bfa;
}

/* ===== Profile Actions ===== */
.profile-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.share-btn,
.chat-invite-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.share-btn:hover,
.chat-invite-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.chat-invite-btn {
  background: rgba(255, 255, 255, 0.25);
}

.chat-invite-btn i {
  font-size: 16px;
}

.chat-invite-btn.chat-exists {
  background: rgba(40, 167, 69, 0.3);
  border-color: rgba(40, 167, 69, 0.5);
}

.chat-invite-btn .online-badge {
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
}

.chat-invite-btn .online-badge i {
  font-size: 8px;
  color: #00ff00;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ===== Chat Modal ===== */
.chat-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.chat-modal {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 450px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.chat-modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.chat-modal-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-modal-icon i {
  font-size: 20px;
}

.chat-modal-header h3 {
  flex: 1;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.chat-modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.chat-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-modal-body {
  padding: 24px;
}

.chat-modal-loading {
  text-align: center;
  padding: 40px 20px;
}

.chat-modal-loading .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

.chat-modal-loading p {
  color: #6b7280;
  margin: 0;
}

.chat-modal-existing {
  text-align: center;
  padding: 20px 0;
}

.existing-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.existing-icon i {
  font-size: 28px;
  color: #00c744;
}

.chat-modal-existing h4 {
  margin: 0 0 8px;
  color: #1f2937;
  font-size: 1.1rem;
}

.chat-modal-existing p {
  margin: 0 0 20px;
  color: #6b7280;
  font-size: 0.9rem;
}

.go-to-chat-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.go-to-chat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 199, 68, 0.3);
}

.chat-modal-form .instructor-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 14px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
}

.chat-modal-form .instructor-preview img {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #00c744;
}

.instructor-preview-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.instructor-preview-name {
  font-weight: 700;
  color: #1f2937;
  font-size: 1.05rem;
}

.instructor-preview-id {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #00c744;
  background: #e8fff1;
  padding: 4px 10px;
  border-radius: 20px;
  font-family: monospace;
}

.instructor-preview-id i {
  font-size: 12px;
}

.confirmation-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  margin-bottom: 16px;
}

.confirmation-box i {
  color: #3b82f6;
  font-size: 18px;
  margin-top: 2px;
}

.confirmation-box p {
  margin: 0;
  color: #1e40af;
  font-size: 0.9rem;
  line-height: 1.5;
}

.chat-modal-form .form-field {
  margin-bottom: 16px;
}

.chat-modal-form .form-field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
}

.chat-modal-form .form-field .optional {
  color: #9ca3af;
  font-weight: 400;
}

.chat-modal-form .form-field textarea {
  width: 100%;
  padding: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  resize: none;
  transition: all 0.3s ease;
  font-family: inherit;
}

.chat-modal-form .form-field textarea:focus {
  outline: none;
  border-color: #00c744;
  box-shadow: 0 0 0 3px rgba(0, 199, 68, 0.1);
}

.chat-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-size: 0.9rem;
}

.chat-success {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #16a34a;
  font-size: 0.9rem;
}

.chat-modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
}

.chat-modal-footer .cancel-btn {
  flex: 1;
  padding: 14px;
  background: #f3f4f6;
  border: none;
  border-radius: 12px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chat-modal-footer .cancel-btn:hover {
  background: #e5e7eb;
}

.chat-modal-footer .send-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chat-modal-footer .send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 199, 68, 0.3);
}

.chat-modal-footer .send-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Dark mode for chat modal */
[data-bs-theme="dark"] .chat-modal,
[data-theme="dark"] .chat-modal,
.dark .chat-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .chat-modal-existing h4,
[data-theme="dark"] .chat-modal-existing h4,
.dark .chat-modal-existing h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .chat-modal-existing p,
[data-theme="dark"] .chat-modal-existing p,
.dark .chat-modal-existing p,
[data-bs-theme="dark"] .chat-modal-loading p,
[data-theme="dark"] .chat-modal-loading p,
.dark .chat-modal-loading p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .chat-modal-form .instructor-preview,
[data-theme="dark"] .chat-modal-form .instructor-preview,
.dark .chat-modal-form .instructor-preview {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
  border-color: #475569;
}

[data-bs-theme="dark"] .instructor-preview-name,
[data-theme="dark"] .instructor-preview-name,
.dark .instructor-preview-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .confirmation-box,
[data-theme="dark"] .confirmation-box,
.dark .confirmation-box {
  background: #1e3a5f;
  border-color: #2563eb;
}

[data-bs-theme="dark"] .confirmation-box p,
[data-theme="dark"] .confirmation-box p,
.dark .confirmation-box p {
  color: #93c5fd;
}

[data-bs-theme="dark"] .chat-modal-form .form-field label,
[data-theme="dark"] .chat-modal-form .form-field label,
.dark .chat-modal-form .form-field label {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .chat-modal-form .form-field textarea,
[data-theme="dark"] .chat-modal-form .form-field textarea,
.dark .chat-modal-form .form-field textarea {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .chat-modal-footer,
[data-theme="dark"] .chat-modal-footer,
.dark .chat-modal-footer {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .chat-modal-footer .cancel-btn,
[data-theme="dark"] .chat-modal-footer .cancel-btn,
.dark .chat-modal-footer .cancel-btn {
  background: #334155;
  color: #e2e8f0;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .profile-hero {
    padding: 40px 20px;
  }

  .profile-avatar {
    width: 120px;
    height: 120px;
  }

  .profile-name {
    font-size: 1.5rem;
  }

  .profile-stats {
    gap: 12px;
  }

  .stat-card {
    padding: 16px 20px;
    min-width: auto;
    flex: 1;
  }

  .stat-number {
    font-size: 22px;
  }

  .tabs-header {
    flex-direction: column;
  }

  .items-grid {
    grid-template-columns: 1fr;
  }

  .profile-actions {
    flex-direction: column;
    gap: 10px;
  }

  .share-btn,
  .chat-invite-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
